import React, { FC, useState } from 'react'
import { I18n } from 'aws-amplify'
import classnames from 'classnames'
import { Grid, makeStyles, Theme, Typography } from '@material-ui/core'
import { useHistory } from 'react-router-dom'

import { GameFlowSteps, EventStages } from 'types'
import { RouteTransition } from 'components'
import { GameFlowStepsConfig } from 'helpers/steps'
import { useAppState } from 'providers'

import { ReactComponent as LiveIcon } from 'assets/live-icon.svg'
import { createInteraction } from 'helpers'

interface NavigationProps {
  activeTab: GameFlowSteps
  setActiveTab: (state: GameFlowSteps) => void
  notifyESS: () => void // EventStartingSoon
  streamStartTime?: string
  eventStage?: EventStages
  isSession: boolean
  setIsSession: (bool: boolean) => void
  isEventReplay: boolean
  setIsEventReplay: (bool: boolean) => void
}

export const Navigation: FC<NavigationProps> = ({
  activeTab,
  setActiveTab,
  notifyESS,
  streamStartTime,
  eventStage,
  isSession,
  setIsSession,
  isEventReplay,
  setIsEventReplay
}) => {
  const history = useHistory()
  const {
    appState: { user, showLivestream, isNavigating },
    setNavigating
  } = useAppState()
  const [previousState, setPreviousState] = useState<GameFlowSteps>(activeTab)
  // eslint-disable-next-line
  const [redirectTrigger, setRedirectTrigger] = useState<boolean>(false)

  const classes = useStyles()

  const onClick = (state: GameFlowSteps) => {
    createInteraction(state, user?.email || '', 'navigation', 'clicked', eventStage)
    if (!isNavigating) {
      setActiveTab(state)

      const welcomeScenes = [GameFlowSteps.Welcome, GameFlowSteps.Intro, GameFlowSteps.Connect]

      const animationScenes = [GameFlowSteps.Demo, GameFlowSteps.Sessions, GameFlowSteps.Explore]

      if (
        (!welcomeScenes.includes(state) && welcomeScenes.includes(previousState)) ||
        animationScenes.includes(state) ||
        animationScenes.includes(previousState)
      ) {
        setNavigating(true)
        setTimeout(() => {
          setNavigating(false)
        }, GameFlowStepsConfig[activeTab].animation.time)
      }

      setPreviousState(state)
    }
  }

  return (
    <>
      <div id='topNavContainer' className={classes.topNavContainer}>
        <Grid item classes={{ root: classes.navigationSectionOfHeader }}>
          <Typography
            component='span'
            variant='h5'
            className={
              activeTab === GameFlowSteps.Welcome || activeTab === GameFlowSteps.Intro
                ? classes.navItemActive
                : classes.navItem
            }
            onClick={() => onClick(GameFlowSteps.Welcome)}
          >
            Welcome
          </Typography>
          {eventStage === EventStages.POSTEVENT && (
            <Typography
              component='span'
              variant='h5'
              className={activeTab === GameFlowSteps.Demo && isEventReplay ? classes.navItemActive : classes.navItem}
              onClick={() => {
                setIsSession(false)
                window.postMessage(JSON.stringify({ command: 'eventReplay', param: 1 }), '*')
              }}
            >
              Event replay
            </Typography>
          )}
          <Typography
            component='span'
            variant='h5'
            className={
              [GameFlowSteps.Sessions, GameFlowSteps.BackToSessions].includes(activeTab) ||
              (activeTab === GameFlowSteps.Demo && isSession)
                ? classes.navItemActive
                : classes.navItem
            }
            onClick={() => {
              setIsEventReplay(false)
              onClick(GameFlowSteps.Sessions)
              setIsSession(true)
            }}
          >
            Public Safety use cases
          </Typography>
          <Typography
            component='span'
            variant='h5'
            className={
              [GameFlowSteps.Explore, GameFlowSteps.BackToExplore].includes(activeTab) ||
              (activeTab === GameFlowSteps.Demo && !isSession && !isEventReplay)
                ? classes.navItemActive
                : classes.navItem
            }
            onClick={() => {
              onClick(GameFlowSteps.Explore)
              setIsSession(false)
              setIsEventReplay(false)
            }}
          >
            5G use cases
          </Typography>
          <Typography
            component='span'
            variant='h5'
            className={activeTab === GameFlowSteps.Connect ? classes.navItemActive : classes.navItem}
            onClick={() => onClick(GameFlowSteps.Connect)}
          >
            Contact us
          </Typography>
          {eventStage !== EventStages.POSTEVENT && (
            <Typography
              component='span'
              variant='h5'
              className={classnames(
                classes.navItem,
                classes.livestreamItem,
                !showLivestream && classes.livestreamDisabled
              )}
              onClick={() => {
                createInteraction('livestream', user?.email || '', 'navigation', 'clicked', eventStage)
                history.push('/stream')
              }}
            >
              <span className={classes.liveIcon}>
                <LiveIcon width={30} height={20} className={classnames(showLivestream && 'pulse')} />
                {I18n.get('liveStream')}
              </span>
            </Typography>
          )}
        </Grid>
      </div>
      <RouteTransition route='/stream' animationTrigger={redirectTrigger} timeout={300} />
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  topNavContainer: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center'
  },
  navigationSectionOfHeader: {
    display: 'flex',
    alignItems: 'center',
    height: '30px',

    [theme.breakpoints.down('md')]: {
      display: 'flex'
    },
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center'
    }
  },
  navItem: {
    marginRight: '28px',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '20px',
    color: '#000000',
    cursor: 'pointer',

    [theme.breakpoints.down('md')]: {
      fontSize: '0.9rem',
      marginRight: '0.5rem'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  navItemActive: {
    marginRight: '28px',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '20px',
    color: '#000000',
    cursor: 'pointer',
    borderBottom: '3px solid #CD040B',

    [theme.breakpoints.down('md')]: {
      fontSize: '0.9rem',
      marginRight: '0.5rem'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  lineBreak: {
    border: '2px solid #D52B1E'
  },
  countdownContainer: {
    position: 'relative',
    display: 'inline-flex',
    transition: '200ms width ease',

    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      right: 0,
      marginTop: '-26px',
      marginRight: '32px',
      width: '305px',
      display: 'block',
      fontSize: '0.9rem'
    },
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'nowrap',
      marginTop: 'unset'
    }
  },
  livestreamItem: {
    marginLeft: 50
  },
  liveIcon: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: '.5rem'
    }
  },
  livestreamDisabled: {
    color: '#979797',
    '& svg': {
      '& .icon-bg': {
        fill: '#979797 !important'
      }
    }
  }
}))
